/**
 * THE SEPARATOR IS PART OF THE REF, and it is the whole point of this module.
 *
 * Refs used to be built by gluing normalised ancestor labels together with nothing between them, which made two
 * unrelated nesting shapes indistinguishable: task `1` → subtask `1` → subtask `b` and a top-level task `11`
 * with a direct subtask `b` both came out as `"11b"`. A re-grade of either then silently matched the other, and
 * the canvas lit the wrong box. Keeping a separator that survives normalisation makes the two `"1.1.b"` and
 * `"11.b"`, which cannot collide.
 *
 * `normalizeTaskRefSegment` is therefore the only thing allowed to touch a single label (it strips every
 * non-alphanumeric character, separators included), and joining is always done through this constant.
 */
export const TASK_REF_SEPARATOR = '.';

/**
 * One task label reduced to its bare form: `"Aufgabe 1"` → `"1"`, `"a)"` → `"a"`. Never a whole path.
 *
 * `teilaufgabe` is stripped BEFORE `aufgabe`, which it contains: the other order left `"teil"` behind on every
 * "Teilaufgabe 1.1.b" a teacher typed, and that stub matched no task.
 */
export const normalizeTaskRefSegment = (value: string | undefined | null): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/teilaufgabe/g, '')
    .replace(/aufgabe/g, '')
    .replace(/task/g, '')
    .replace(/\s+/g, '')
    .replace(/[^0-9a-z]/g, '')
    .trim();

/** Already-split path segments → the canonical comparable ref: `["1", "1a", "i"]` → `"1.1a.i"`. */
export const joinTaskRefSegments = (segments: Array<string | undefined | null>): string =>
  segments.map(normalizeTaskRefSegment).filter(Boolean).join(TASK_REF_SEPARATOR);

/**
 * Appends ONE segment to an already-joined ref, for the tree walks that accumulate a path node by node.
 *
 * They cannot use `joinTaskRefSegments` for this: it normalises every element it is given, and running
 * `normalizeTaskRefSegment` over an accumulated `"1.a"` would strip the separator straight back out.
 */
export const appendTaskRefSegment = (path: string, segment: string | undefined | null): string => {
  const normalized = normalizeTaskRefSegment(segment);
  if (!path) return normalized;
  if (!normalized) return path;
  return `${path}${TASK_REF_SEPARATOR}${normalized}`;
};

/**
 * A ref in ANY of the spellings this system produces — a teacher's free text (`"Aufgabe 1.1.b"`), the chat
 * tool's canonical dotted path (`"1.1.b"`) or a `"|"`-qualified path out of `dependsOn` (`"1|1|b"`) — reduced
 * to the one comparable form `"1.1.b"`.
 *
 * Splitting on both separators is what lets `dependsOn` stay pipe-joined in the database with no migration.
 */
export const normalizeTaskRef = (value: string | undefined | null): string =>
  joinTaskRefSegments(String(value ?? '').split(/[.\|]/v));

export const normalizeTaskRefs = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.map(normalizeTaskRef).filter(Boolean)));

export const toNormalizedTaskRefSet = (values: Array<string | undefined | null>): Set<string> =>
  new Set(normalizeTaskRefs(values));

/**
 * Every ancestor-inclusive prefix of a "|"-joined qualified task path, normalised:
 * `"1|1a|i"` → `["1", "1.1a", "1.1a.i"]`.
 *
 * A requested ref matches a node when it equals ANY of these, which is what makes a request for a parent cover
 * its whole subtree while a request for one leaf covers only that leaf.
 *
 * THE ONE PLACE THE ACCUMULATION IS WRITTEN, because two sides have to agree exactly on it: the server decides a
 * node's real grading scope with it (`qualifiedPathMatchesRequestedRefs`, mirroring `shouldGradeByFilter`), and
 * the canvas decides from the same refs whether that node's box is busy. If those two ever disagreed in the
 * strict direction, a box being regraded would show no overlay and stay editable, and the teacher's edit would
 * be silently overwritten.
 *
 * It lives in this leaf module rather than beside either consumer because the client cannot import the server's
 * grading agent — that module statically pulls in the LLM graph — and a second copy of the walk is exactly how
 * the two would drift.
 */
export const cumulativeTaskRefs = (qualifiedTaskPath: string | undefined | null, separator = '|'): string[] => {
  const refs: string[] = [];
  const segments: string[] = [];
  for (const segment of String(qualifiedTaskPath ?? '').split(separator)) {
    const normalized = normalizeTaskRefSegment(segment);
    if (!normalized) continue;
    segments.push(normalized);
    refs.push(segments.join(TASK_REF_SEPARATOR));
  }
  return refs;
};
